import { useEffect, useRef } from "react";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS =
  (window.i2290settings && window.i2290settings.CCTimeOutInSeconds) || 60;
const POLL_TIMEOUT_ERROR_MESSAGE =
  (window.i2290settings && window.i2290settings.CCTimeOutErrorMessage) ||
  "Something happened. Please try again later";
const TERMINAL_PHASES = new Set(["succeeded", "failed"]);

/**
 * Polls GET /api/payments/status/{attemptId} on a ~2s cadence while the
 * payment phase is non-terminal. Ceases automatically when:
 *   - phase transitions to "succeeded" or "failed"
 *   - attemptId becomes null/undefined (retry reset or modal close)
 *   - POLL_TIMEOUT_MS elapses without a terminal result (dispatches "failed")
 *   - the component that owns this hook unmounts
 *
 * @param {number|null} attemptId
 * @param {string} phase - current payment phase from redux state
 * @param {function} dispatchGetPaymentStatus - bound action creator (attemptId) => dispatch(getPaymentStatus(attemptId))
 * @param {function} dispatchSetPaymentPhase - bound action creator (phase, error) => dispatch(setPaymentPhase(phase, error))
 */
export function usePaymentAttemptPoll(attemptId, phase, dispatchGetPaymentStatus, dispatchSetPaymentPhase) {
  const timerRef = useRef(null);
  const deadlineRef = useRef(null);

  useEffect(() => {
    const shouldPoll = attemptId != null && !TERMINAL_PHASES.has(phase);

    if (!shouldPoll) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (deadlineRef.current) {
        clearTimeout(deadlineRef.current);
        deadlineRef.current = null;
      }
      return;
    }

    let cancelled = false;

    deadlineRef.current = setTimeout(() => {
      if (cancelled) return;
      console.log("[poll] timeout reached for attemptId", attemptId, "— forcing failed"); // CLEANUP
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      dispatchSetPaymentPhase("failed", { kind: "status", message: POLL_TIMEOUT_ERROR_MESSAGE });
    }, POLL_TIMEOUT_MS);

    const schedulePoll = () => {
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        console.log("[poll] firing for attemptId", attemptId, "phase", phase); // CLEANUP
        await dispatchGetPaymentStatus(attemptId);
        console.log("[poll] dispatch complete, scheduling next if not cancelled:", !cancelled); // CLEANUP
        if (!cancelled) schedulePoll();
      }, POLL_INTERVAL_MS);
    };

    schedulePoll();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (deadlineRef.current) {
        clearTimeout(deadlineRef.current);
        deadlineRef.current = null;
      }
    };
  }, [attemptId, phase, dispatchGetPaymentStatus, dispatchSetPaymentPhase]);
}
